import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { formatPrice } from '../../../utils';
import { useAuth } from '../../../contexts/AuthContext';
import { supabase } from '../../../utils/supabase';

const SavedVendors = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [savedVendors, setSavedVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSavedVendors = async () => {
      if (!user?.id) return;

      try {
        // Get user's saved vendors from saved_vendors table
        const { data: favorites, error } = await supabase
          .from('saved_vendors')
          .select(`
            vendor_id,
            vendor_profiles!inner (
              id,
              business_name,
              category,
              city,
              state,
              starting_price,
              is_verified
            )
          `)
          .eq('user_id', user.id);

        if (error) throw error;

        const vendors = favorites?.map(fav => ({
          id: fav.vendor_profiles.id,
          name: fav.vendor_profiles.business_name,
          category: fav.vendor_profiles.category,
          location: `${fav.vendor_profiles.city}, ${fav.vendor_profiles.state}`,
          rating: 0, // Will be calculated from reviews
          reviewCount: 0, // Will be calculated from reviews
          startingPrice: fav.vendor_profiles.starting_price || 0,
          image: '/assets/images/no_image.png', // Default image
          verified: fav.vendor_profiles.is_verified || false,
          isFavorite: true
        })) || [];

        setSavedVendors(vendors);
      } catch (error) {
        console.error('Error loading saved vendors:', error);
      } finally {
        setLoading(false);
      }
    };

    loadSavedVendors();
  }, [user]);

  const handleToggleFavorite = async (vendorId) => {
    if (!user?.id) return;

    // Remove from favorites in Supabase
    const { error } = await supabase
      .from('saved_vendors')
      .delete()
      .eq('user_id', user.id)
      .eq('vendor_id', vendorId);

    if (!error) {
      // Update local state
      setSavedVendors(prev => prev.filter(v => v.id !== vendorId));
    }
  };

  const handleRemoveFavorite = (vendorId) => {
    handleToggleFavorite(vendorId);
  };

  const handleViewVendor = (vendor) => {
    navigate('/vendor-profile', {
      state: {
        vendorId: vendor.id,
        vendorName: vendor.name,
        category: vendor.category
      }
    });
  };

  const handleContactVendor = (vendor) => {
    navigate('/vendor-profile', {
      state: {
        vendorId: vendor.id,
        vendorName: vendor.name,
        category: vendor.category,
        openContact: true
      }
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-soft p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="font-heading font-semibold text-xl text-text-primary">
          Saved Vendors
        </h3>
        <Button
          variant="outline"
          size="sm"
          iconName="Plus"
          onClick={() => navigate('/all-vendors')}
        >
          Add More
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {savedVendors?.map((vendor) => (
          <div key={vendor?.id} className="border border-border-light rounded-lg p-4 hover:shadow-medium transition-all duration-200">
            <div className="relative mb-3">
              <img
                src={vendor?.image}
                alt={vendor?.name}
                className="w-full h-32 rounded-lg object-cover"
              />
              <button
                onClick={() => handleToggleFavorite(vendor?.id)}
                className={`absolute top-2 right-2 p-2 rounded-full transition-all duration-200 ${vendor?.isFavorite
                    ? 'bg-red-500 text-white shadow-soft'
                    : 'bg-white/80 text-gray-400 hover:bg-white hover:text-red-500'
                  }`}
              >
                <Icon
                  name="Heart"
                  size={16}
                  fill={vendor?.isFavorite ? 'currentColor' : 'none'}
                />
              </button>
              {vendor?.verified && (
                <div className="absolute top-2 left-2 bg-green-500 text-white px-2 py-1 rounded-full text-xs font-medium">
                  <Icon name="CheckCircle" size={12} className="inline mr-1" />
                  Verified
                </div>
              )}
            </div>

            <div className="space-y-2">
              <h4 className="font-semibold text-text-primary line-clamp-1">
                {vendor?.name}
              </h4>
              <p className="text-text-secondary text-sm">{vendor?.category}</p>
              <p className="text-text-muted text-sm flex items-center">
                <Icon name="MapPin" size={14} className="mr-1" />
                {vendor?.location}
              </p>

              <div className="flex items-center space-x-2">
                <div className="flex items-center space-x-1">
                  <Icon name="Star" size={14} className="text-yellow-500" fill="currentColor" />
                  <span className="text-sm font-medium text-text-primary">{vendor?.rating}</span>
                </div>
                <span className="text-text-muted text-sm">({vendor?.reviewCount} reviews)</span>
              </div>

              <div className="flex items-center justify-between pt-2">
                <span className="text-primary font-semibold">
                  From {formatPrice(vendor?.startingPrice)}
                </span>
              </div>

              <div className="flex space-x-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleViewVendor(vendor)}
                  className="flex-1"
                >
                  View
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => handleContactVendor(vendor)}
                  className="flex-1"
                >
                  Contact
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {savedVendors?.length === 0 && (
        <div className="text-center py-8">
          <Icon name="Heart" size={48} className="text-text-muted mx-auto mb-4" />
          <p className="text-text-secondary mb-4">No saved vendors yet</p>
          <Button
            variant="primary"
            size="sm"
            iconName="Search"
            onClick={() => navigate('/all-vendors')}
          >
            Find Vendors
          </Button>
        </div>
      )}
    </div>
  );
};

export default SavedVendors;